import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Col, notification, Row } from 'antd';
import { config } from 'config/config';
import routePaths, { staticRoutes } from 'config/routes';
import React, { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { postDataCall } from 'services/api';
import apiPaths from 'services/apiPaths';
import { updateMCSubscription, updateProfiling } from 'src/actions/authActions';
import CustomModal from 'src/components/CustomModal';
import Link from 'src/components/Link';
import { MCUserStatus } from 'src/shared/enums';
import { IUser } from 'src/shared/models';
import { customFormatMessage, useWindowSize } from 'utils';

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      updateMCSubscription,
      updateProfiling,
    },
    dispatch
  );


interface OwnProps {
  profilingAccepted: null | boolean;
  setProfilingAccepted: React.Dispatch<React.SetStateAction<boolean>>;
  user: IUser | null;
  hideModal: boolean;
}

type Props = OwnProps & ReturnType<typeof mapDispatchToProps>;

const ValidateNewsleterProfiling: FC<Props> = ({
  profilingAccepted,
  setProfilingAccepted,
  user,
  updateMCSubscription,
  updateProfiling,
  hideModal,
}) => {
  const { PLATFORM } = config.APP;
  const { formatMessage } = useIntl();
  const { isMD } = useWindowSize();
  const isMobile = isMD;
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [error, setError] = useState(null);
  const getCurrentUrl = () => { return window.location.href }

  useEffect(() => {
    if (!modalVisible && profilingAccepted === false && hideModal === false && user.termsAccepted)
      setModalVisible(true);
  }, [profilingAccepted, hideModal, user]);

  const handleCloseModal = () => {
    updateProfiling();
    setProfilingAccepted(true);
    setModalVisible(false);
  };

  const handleValidate = async () => {
    try {
      setLoading(true);
      handleCloseModal();

      await postDataCall({
        dataPath: apiPaths.MC_NEWSLETTER.SUBSCRIBE,
        data: {
            isDermo: user.dermo.isDermo,
            currentUrl: getCurrentUrl()
        },
        callConfig: {},
      });
      updateMCSubscription(MCUserStatus.SUBSCRIBED, MCUserStatus.SUBSCRIBED);

      notification['success']({
        message: formatMessage({
          id: `page.account.newsletter.subscribe`,
        }),
      });
    } catch (error) {
      setError(error.response?.data?.error);
    } finally {
      setModalVisible(false);
      setLoading(false);
    }
  };

  const renderTitle = (
    <h3 className="newsletter-modal-content--title">
      {formatMessage({ id: 'register.profiling-modal.content-title' })}
    </h3>
  );

  const renderSubtitle = (
    <p className="newsletter-modal-content--subtitle">
      {formatMessage({ id: 'register.profiling-modal.content-subtitle' })}
    </p>
  );

  const renderImage = () => {
    if (!isMobile) {
      return (
        <Col sm={0} md={12}>
          <img
            alt=""
            loading="lazy"
            className="newsletter-modal-image"
            src={`${config.APP.MEDIA_CON}isdin_newsletter_profiling.png`}
          />
        </Col>
      );
    }
  };

  const renderButton = (
    <Row>
      <Button
        onClick={handleValidate}
        className="loginForm__btnLogin btn-primary btn-primary--black newsletter-modal-content--btn"
        loading={loading}
        block
      >
        {formatMessage({ id: 'app.accept' })}
      </Button>
    </Row>
  );

  const renderTerms = (
    <Row className="newsletter-modal-content--terms">
      <p>
        {customFormatMessage('register.profiling-modal.content-terms', {
          privacy: function setPrivacy(chunks: any) {
            return (
              <Link
                href={`${routePaths.STATIC}?page=${staticRoutes.FOOTER.PRIVACY_POLICY}`}
                passHref
              >
                <a target="_blank" className="link underline-text">
                  {chunks}
                </a>
              </Link>
            );
          },
        })}
      </p>
    </Row>
  );

  const renderDescription = (numberList: number) => {
    return (
      <>
        {[...Array.from(Array(numberList).keys())].map((num, key) => (
          <Row
            key={`newsletter-modal-content-${key}`}
            className="newsletter-modal-content--row-description"
          >
            <div className="newsletter-modal-content--circle">
              <CheckCircleOutlined
                style={{ fontSize: '18px', color: '#414141' }}
              />
            </div>
            <p className="newsletter-modal-content--description">
              {formatMessage({
                id: 'register.profiling-modal.content-description' + num,
              })}
            </p>
          </Row>
        ))}
      </>
    );
  };

  const modalContent = (
    <Row justify="center" align="middle">
      {renderImage()}
      <Col sm={24} md={12}>
        <div className="newsletter-modal-content">
          {renderTitle}
          {renderSubtitle}
          {PLATFORM !== 'con-de' ? renderDescription(4) : renderDescription(3)}
          {renderButton}
          {renderTerms}
        </div>
      </Col>
    </Row>
  );

  return (
    <CustomModal
      {...{
        content: modalContent,
        error,
        footer: null,
        loading,
        maskClosable: false,
        onCancel: handleCloseModal,
        showIcon: false,
        visible: modalVisible,
        customClass: 'newsletter-modal',
      }}
    />
  );
};

export default connect(null, mapDispatchToProps)(ValidateNewsleterProfiling);
